import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { ArrowUpRight, Loader2, Plus, RefreshCw, Search, Tags, Trash2, } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DashboardSection } from '@/components/dashboard/DashboardSection';
import { addCategoryKeyword, fetchCategoryMaster, removeCategoryKeyword, } from '@/services/categoriesApi';
import { acquireGraphAccessToken } from '@/services/graph/acquireGraphToken';
import { useCommunications } from '@/context/CommunicationsContext';
const pageContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.06, delayChildren: 0.04 },
    },
};
const pageItem = {
    hidden: { opacity: 0, y: 12 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
    },
};
export function KeywordsPage() {
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();
    const { communications } = useCommunications();
    const [master, setMaster] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [drafts, setDrafts] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            setMaster(await fetchCategoryMaster());
        }
        catch (err) {
            setError(err instanceof Error
                ? `Could not load the keyword master: ${err.message}`
                : 'Could not load the keyword master.');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, []);
    async function token() {
        const account = instance.getActiveAccount() ?? accounts[0] ?? null;
        if (!account)
            throw new Error('Microsoft account is unavailable.');
        return acquireGraphAccessToken(instance, account);
    }
    async function addKeyword(category) {
        const keyword = (drafts[category] || '').trim();
        if (!keyword)
            return;
        setSavingKey(`add:${category}`);
        setError(null);
        try {
            const graphToken = await token();
            setMaster(await addCategoryKeyword(graphToken, category, keyword));
            setDrafts((prev) => ({ ...prev, [category]: '' }));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Could not add keyword.');
        }
        finally {
            setSavingKey(null);
        }
    }
    async function removeKeyword(category, keyword) {
        setSavingKey(`delete:${category}:${keyword}`);
        setError(null);
        try {
            const graphToken = await token();
            setMaster(await removeCategoryKeyword(graphToken, category, keyword));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Could not remove keyword.');
        }
        finally {
            setSavingKey(null);
        }
    }
    const countsByCategory = useMemo(() => {
        const counts = new Map();
        for (const c of communications) {
            for (const cat of c.categories?.length ? c.categories : [c.category]) {
                counts.set(cat, (counts.get(cat) ?? 0) + 1);
            }
        }
        return counts;
    }, [communications]);
    const rows = useMemo(() => {
        if (!master)
            return [];
        const q = query.trim().toLowerCase();
        return master.categories
            .map((category) => ({
            category,
            keywords: master.keywordMaster[category] ?? [],
        }))
            .filter((row) => {
            if (!q)
                return true;
            return (row.category.toLowerCase().includes(q) ||
                row.keywords.some((k) => k.toLowerCase().includes(q)));
        });
    }, [master, query]);
    const totalKeywords = useMemo(() => master
        ? Object.values(master.keywordMaster).reduce((sum, list) => sum + list.length, 0)
        : 0, [master]);
    return (_jsxs(motion.div, { className: "mx-auto flex w-full max-w-[1400px] flex-col gap-8 pb-10", variants: pageContainer, initial: "hidden", animate: "show", children: [_jsx(motion.div, { variants: pageItem, children: _jsx(DashboardSection, { eyebrow: "Classification", title: "Category keyword master", description: "Manage the keyword signals used by AI classification. Add or remove keywords under each fixed master category; changes apply to new and re-run classifications immediately.", aside: master ? (_jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Badge, { variant: "secondary", className: "rounded-md px-2.5 py-1 text-xs tabular-nums", children: [master.categories.length, " categories"] }), _jsxs(Badge, { variant: "secondary", className: "rounded-md px-2.5 py-1 text-xs tabular-nums", children: [totalKeywords, " keywords"] })] })) : undefined }) }), _jsxs(motion.div, { variants: pageItem, className: "flex flex-wrap items-center gap-3", children: [_jsxs("div", { className: "relative min-w-0 flex-1 sm:max-w-md", children: [_jsx(Search, { className: "pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground", "aria-hidden": true }), _jsx(Input, { placeholder: "Search a category or keyword\u2026", value: query, onChange: (e) => setQuery(e.target.value), className: "h-11 rounded-xl border-border/70 bg-background pl-10 pr-4 text-sm shadow-sm" })] }), _jsxs(Button, { variant: "outline", className: "h-11 gap-2 rounded-xl", onClick: () => void load(), disabled: loading, children: [loading ? (_jsx(Loader2, { className: "h-4 w-4 animate-spin", "aria-hidden": true })) : (_jsx(RefreshCw, { className: "h-4 w-4", "aria-hidden": true })), "Reload"] })] }), error ? (_jsx(motion.div, { variants: pageItem, role: "status", className: "rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive shadow-sm", children: error })) : null, loading && !master ? (_jsxs(motion.div, { variants: pageItem, className: "flex min-h-[30vh] items-center justify-center gap-2 text-sm text-muted-foreground", children: [_jsx(Loader2, { className: "h-4 w-4 animate-spin", "aria-hidden": true }), "Loading keyword master\u2026"] })) : null, master && rows.length === 0 && !loading ? (_jsxs(motion.div, { variants: pageItem, className: "rounded-2xl border border-border/60 bg-card p-10 text-center text-sm text-muted-foreground", children: ["No category or keyword matches \u201C", query, "\u201D."] })) : null, rows.length > 0 ? (_jsx(motion.ul, { variants: pageItem, className: "grid gap-4 sm:grid-cols-2 xl:grid-cols-3", children: rows.map((row, i) => {
                    const count = countsByCategory.get(row.category) ?? 0;
                    return (_jsxs(motion.li, { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { delay: Math.min(i * 0.03, 0.4) }, className: "flex flex-col rounded-2xl border border-border/60 bg-card p-5 shadow-sm transition-shadow hover:border-primary/30 hover:shadow-md", children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsxs("div", { className: "flex items-center gap-2.5", children: [_jsx("span", { className: "flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary", children: _jsx(Tags, { className: "h-4 w-4", "aria-hidden": true }) }), _jsxs("div", { children: [_jsx("p", { className: "text-sm font-semibold text-foreground", children: row.category }), _jsxs("p", { className: "text-[11px] text-muted-foreground", children: [row.keywords.length, " keyword signals"] })] })] }), _jsxs("button", { type: "button", onClick: () => navigate(`/inbox?category=${encodeURIComponent(row.category)}`), title: `Open inbox filtered to ${row.category}`, className: "inline-flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-semibold tabular-nums text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary", children: [count, " in inbox", _jsx(ArrowUpRight, { className: "h-3 w-3", "aria-hidden": true })] })] }), _jsx("div", { className: "mt-4 flex flex-wrap gap-1.5", children: row.keywords.length > 0 ? (row.keywords.map((keyword) => (_jsxs("span", { className: "group inline-flex items-center gap-1 rounded-md border border-border bg-background py-0.5 pl-2 pr-1 text-[11px] text-foreground", children: [keyword, _jsx("button", { type: "button", onClick: () => void removeKeyword(row.category, keyword), disabled: savingKey ===
                                                `delete:${row.category}:${keyword}`, "aria-label": `Remove ${keyword} from ${row.category}`, title: "Remove keyword", className: "rounded p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50", children: savingKey ===
                                                `delete:${row.category}:${keyword}` ? (_jsx(Loader2, { className: "h-3 w-3 animate-spin" })) : (_jsx(Trash2, { className: "h-3 w-3" })) })] }, keyword)))) : (_jsx("span", { className: "text-xs text-muted-foreground", children: "No keywords defined \u2014 matched by context only." })) }), _jsxs("form", { className: "mt-auto flex gap-2 border-t border-border/50 pt-4", onSubmit: (event) => {
                                    event.preventDefault();
                                    void addKeyword(row.category);
                                }, children: [_jsx(Input, { "aria-label": `New keyword for ${row.category}`, placeholder: "Add keyword or phrase\u2026", value: drafts[row.category] || '', onChange: (event) => setDrafts((prev) => ({
                                            ...prev,
                                            [row.category]: event.target.value,
                                        })), maxLength: 80, className: "h-9 min-w-0 rounded-lg text-xs" }), _jsxs(Button, { type: "submit", size: "sm", className: "h-9 shrink-0 gap-1.5 rounded-lg", disabled: !drafts[row.category]?.trim() ||
                                            savingKey === `add:${row.category}`, children: [savingKey === `add:${row.category}` ? (_jsx(Loader2, { className: "h-3.5 w-3.5 animate-spin" })) : (_jsx(Plus, { className: "h-3.5 w-3.5" })), "Add"] })] })] }, row.category));
                }) })) : null] }));
}
